import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardTour from '../../components/DashboardTour';
import TransferCalculator from '../../components/TransferCalculator';
import ActivityFeed from '../../components/ActivityFeed';
import { InfoIcon, SendIcon } from '../../assets/icons';
import { formatCurrency } from '../../utils/currencyUtils';
import { hasCompletedDashboardTour, formatBalanceDisplayParts } from '../../utils/userPreferencesHelpers';
import * as balancesActions from '../../store/actions/balances';
import * as transactionsActions from '../../store/actions/transactions';
import * as configActions from '../../store/actions/config';
import * as userActions from '../../store/actions/user';

const PROCESSING_STATUSES = ['Pending', 'Executing', 'Processing'];
const CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]';

function maskIban(iban) {
  if (!iban) return null;
  const normalized = String(iban).replace(/\s/g, '');
  return normalized.length >= 4 ? `·· ${normalized.slice(-4)}` : null;
}

function formatStarted(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function getStatusClassName(status) {
  if (status === 'Executing') return 'bg-[var(--n50)] text-[#2e6bad]';
  return 'bg-[var(--n50)] text-[var(--color-warning)]';
}

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bankAccount, user, preferences, gettingUserPreferences } = useSelector((state) => state.user);
  const { balances, gettingBalances } = useSelector((state) => state.balances);
  const { transactions, gettingTransactions } = useSelector((state) => state.transactions);
  const [tourActive, setTourActive] = useState(false);
  const [tourRunId, setTourRunId] = useState(0);

  useEffect(() => {
    dispatch(balancesActions.getBalances());
    dispatch(transactionsActions.getTransactions());
    dispatch(configActions.getConfig());
    dispatch(userActions.getBankAccount());
  }, [dispatch]);

  useEffect(() => {
    if (user?.isOnboarding || gettingUserPreferences) return undefined;
    if (hasCompletedDashboardTour(preferences)) return undefined;

    const timer = window.setTimeout(() => {
      setTourRunId((id) => id + 1);
      setTourActive(true);
    }, 600);

    return () => window.clearTimeout(timer);
  }, [user?.isOnboarding, gettingUserPreferences, preferences]);

  const processingTransactions = useMemo(() => {
    const list = Array.isArray(transactions) ? transactions : Object.values(transactions || {});
    return list.filter((t) => PROCESSING_STATUSES.includes(t.status)).slice(0, 5);
  }, [transactions]);

  const balance = balances?.EUR || 0;
  const { whole, fraction } = formatBalanceDisplayParts(balance, preferences);
  const ibanMasked = maskIban(bankAccount?.iban);
  const hasPending = processingTransactions.length > 0;

  const startTour = () => {
    setTourRunId((id) => id + 1);
    setTourActive(true);
  };

  const tourTriggerButton = (
    <button
      type='button'
      onClick={startTour}
      aria-label='Start dashboard tour'
      title='Dashboard tour'
      className='flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--white)] text-[var(--text-secondary)] transition-[background,color] duration-150 hover:bg-[var(--n50)] hover:text-[var(--n800)]'
    >
      <InfoIcon className='h-[18px] w-[18px]' strokeWidth={1.6} />
    </button>
  );

  return (
    <Layout hideHeader mainPaddingClassName='px-6 py-9 sm:px-11' topBarExtra={tourTriggerButton}>
      <div className='space-y-10'>
        <DashboardHeader />

        <section data-tour='dashboard-overview'>
          <p className='section-label mb-3'>Overview</p>
          <div className='flex flex-wrap items-end justify-between gap-8'>
            <div>
              <p className='mb-2 text-[13px] text-[var(--text-tertiary)]'>Available balance</p>
              <div className='flex items-baseline gap-2.5'>
                <span className='font-display text-5xl font-light leading-none tracking-[-0.025em] text-[var(--n900)] md:text-[64px]'>
                  {gettingBalances ? '—' : `${whole}${fraction} €`}
                </span>
                <span className='text-sm font-medium text-[var(--text-tertiary)]'>EUR</span>
              </div>
              {ibanMasked && <p className='mt-3 text-[13px] text-[var(--text-secondary)]'>IBAN {ibanMasked}</p>}
            </div>
            <div className='flex gap-2.5'>
              <button
                type='button'
                onClick={() => navigate('/accounts')}
                className='inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-5 py-3 text-sm font-medium text-[var(--n800)] hover:bg-[var(--off)]'
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M12 5v14M5 12h14' />
                </svg>
                Add money
              </button>
              <button
                type='button'
                onClick={() => navigate('/send-money')}
                className='inline-flex items-center gap-2 rounded-lg bg-[var(--n800)] px-5 py-3 text-sm font-medium text-white shadow-[var(--shadow-cta)] hover:opacity-90'
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M5 12h14M12 5l7 7-7 7' />
                </svg>
                Send money
              </button>
            </div>
          </div>
        </section>

        <TransferCalculator />

        {hasPending && (
          <section>
            <div className='mb-3 flex items-center justify-between gap-3'>
              <p className='section-label'>Pending transactions</p>
              <button type='button' onClick={() => navigate('/transaction-history')} className='text-xs font-medium text-[var(--n500)] hover:underline'>
                View history →
              </button>
            </div>
            <div className={`${CARD_CLASS} overflow-hidden`}>
              {gettingTransactions && (
                <div className='space-y-0'>
                  <div className='h-16 animate-pulse bg-[var(--off)]' />
                  <div className='h-16 animate-pulse border-t border-[var(--color-border)] bg-[var(--off)]' />
                </div>
              )}
              {!gettingTransactions &&
                processingTransactions.map((transaction, index) => (
                  <div
                    key={transaction.requestId}
                    className={`px-6 py-4 ${index < processingTransactions.length - 1 ? 'border-b border-[var(--color-border)]' : ''}`}
                  >
                    <div className='grid items-center gap-4 text-sm sm:grid-cols-[1.6fr,1fr,1fr,1fr,auto]'>
                      <div>
                        <p className='field-label mb-2'>Request ID</p>
                        <div className='flex flex-wrap items-center gap-3'>
                          <p className='max-w-[170px] truncate font-mono text-sm font-medium text-[var(--n800)]' title={transaction.requestId}>
                            {transaction.requestId}
                          </p>
                          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${getStatusClassName(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className='field-label mb-1'>Amount in</p>
                        <p className='font-medium tabular-nums text-[var(--n800)]'>{formatCurrency(transaction.amountIn, transaction.baseCurrency, preferences)}</p>
                      </div>
                      <div>
                        <p className='field-label mb-1'>Amount out</p>
                        <p className='font-medium tabular-nums text-[var(--n800)]'>{formatCurrency(transaction.amountOut, transaction.quoteCurrency, preferences)}</p>
                      </div>
                      <div>
                        <p className='field-label mb-1'>Started</p>
                        <p className='font-medium text-[var(--n800)]'>{formatStarted(transaction.started)}</p>
                      </div>
                      <div className='flex sm:justify-end'>
                        <button
                          type='button'
                          onClick={() =>
                            navigate(`/transaction-progress/${transaction.requestId}`, {
                              state: { from: '/dashboard', transaction },
                            })
                          }
                          className='inline-flex items-center gap-2 rounded-lg bg-[var(--n800)] px-4 py-2 text-sm font-medium text-white hover:opacity-90'
                        >
                          <SendIcon className='h-4 w-4' />
                          Track
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        <ActivityFeed transactions={transactions} loading={gettingTransactions} />

        <DashboardTour key={tourRunId} active={tourActive} onClose={() => setTourActive(false)} />
      </div>
    </Layout>
  );
}

export default Dashboard;
